import { Info } from "@phosphor-icons/react";
import {
  Table,
  Text,
  Tooltip,
  Link as RadixLink,
  Flex,
} from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { type FC } from "react";
import { useTranslation } from "react-i18next";

import { type AutomationTask, type AutomationStatus } from "../mockData";

interface AutomationTableProps {
  tasks: AutomationTask[];
  startDate?: string;
  endDate?: string;
}

const STATUS_ORDER: AutomationStatus[] = [
  "Fully Automated",
  "In Training",
  "Out of Scope",
];

function getStatusStyles(status: AutomationStatus) {
  switch (status) {
    case "Fully Automated":
      return {
        dotColor: "bg-grass-grass10",
        rowBg: "",
      };
    case "In Training":
      return {
        dotColor: "bg-puntt-gold-10",
        rowBg: "bg-puntt-accent-2",
      };
    case "Out of Scope":
      return {
        dotColor: "bg-puntt-neutral-gray-6",
        rowBg: "bg-puntt-neutral-gray-2",
      };
    default:
      return {
        dotColor: "bg-puntt-neutral-gray-6",
        rowBg: "",
      };
  }
}

// Helper function to format review speed
function formatReviewSpeed(seconds: number): string {
  if (seconds === 0) return "—"; // No data available
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round((seconds / 60) * 10) / 10;
  return `${minutes} min`;
}

// Helper function to format weeks (shows days if less than 1 week)
function formatWeeks(weeks: number): string {
  if (weeks === 0) return "0 days";

  // If less than 1 week, show days
  if (weeks < 1) {
    const days = Math.round(weeks * 7);
    if (days === 0) return "0 days";
    if (days === 1) return "1 day";
    return `${days} days`;
  }

  // For 1 week or more, show whole weeks
  const wholeWeeks = Math.floor(weeks);
  if (wholeWeeks === 1) return "1 week";
  return `${wholeWeeks} weeks`;
}

// Progress bar component for error-free checks
interface ProgressBarProps {
  value: number;
  max: number;
  status: AutomationStatus;
}

function ProgressBar({ value, max, status }: ProgressBarProps) {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;

  const getProgressColor = () => {
    switch (status) {
      case "Fully Automated":
        return "bg-grass-grass10";
      case "In Training":
        return "bg-puntt-gold-10";
      case "Out of Scope":
        return "bg-puntt-neutral-gray-6";
      default:
        return "bg-puntt-neutral-gray-6";
    }
  };

  return (
    <Flex direction="column" gap="1" className="min-w-[100px]">
      <div className="h-2 w-full overflow-hidden rounded-full bg-puntt-neutral-gray-4">
        <div
          className={cx(
            "h-full transition-all duration-300",
            getProgressColor(),
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <Text
        size="1"
        className="whitespace-nowrap tabular-nums text-puntt-neutral-gray-9"
      >
        {value}/{max}
      </Text>
    </Flex>
  );
}

interface TaskRowProps {
  task: AutomationTask;
  index: number;
  startDate?: string;
  endDate?: string;
}

function TaskRow({ task, index, startDate, endDate }: TaskRowProps) {
  const styles = getStatusStyles(task.status);
  const isOutOfScope = task.status === "Out of Scope";
  // Determine row background:
  // - Out of Scope rows get their specific background
  // - All other rows use alternating pattern (odd rows get gray-2)
  const isOddRow = index % 2 === 1;
  const rowBackground = isOutOfScope
    ? styles.rowBg
    : isOddRow
      ? "bg-puntt-neutral-gray-2"
      : "bg-base-white";

  return (
    <Table.Row
      className={cx("border-b border-puntt-neutral-gray-6", rowBackground)}
      data-testid="roi-table-row"
    >
      {/* Task Type */}
      <Table.Cell data-testid="roi-category-name">
        <Flex align="center" gap="1" className="w-fit">
          <Text size="2" weight="medium">
            {task.taskCategory}
          </Text>
          {task.description && (
            <Tooltip content={task.description}>
              <Info
                size={14}
                className="shrink-0 cursor-help text-puntt-neutral-gray-9"
              />
            </Tooltip>
          )}
        </Flex>
      </Table.Cell>

      {/* Tasks Done */}
      <Table.Cell>
        <Text size="2" className="tabular-nums">
          {isOutOfScope ? "—" : task.auditLog}
        </Text>
      </Table.Cell>

      {/* Low Risk Human Caught Issues */}
      <Table.Cell>
        <Text size="2" className="tabular-nums">
          {isOutOfScope ? "—" : task.humanCaughtLowRisk}
        </Text>
      </Table.Cell>

      {/* High Risk Human Caught Issues */}
      <Table.Cell>
        <Text size="2" className="tabular-nums">
          {isOutOfScope ? "—" : task.humanCaughtHighRisk}
        </Text>
      </Table.Cell>

      {/* Weeks without High Risk Error */}
      <Table.Cell>
        <Text size="2">
          {isOutOfScope ? "N/A" : formatWeeks(task.weeksWithoutHighRisk)}
        </Text>
      </Table.Cell>

      {/* AI Review Speed */}
      <Table.Cell>
        <Text size="2">
          {isOutOfScope ? "N/A" : formatReviewSpeed(task.aiReviewSpeed)}
        </Text>
      </Table.Cell>

      {/* Audit Log */}
      <Table.Cell>
        {isOutOfScope || task.auditLog === 0 ? (
          <Text size="2" className="text-puntt-neutral-gray-9">
            —
          </Text>
        ) : (
          <RadixLink asChild>
            <Link
              to="/roi/audit"
              search={{
                atomicTasks: task.categoryId,
                atomicTaskFilterMode: "includes",
                startDate,
                endDate,
              }}
              className="font-medium text-puntt-accent-9 underline decoration-puntt-accent-9"
            >
              {task.auditLog}
            </Link>
          </RadixLink>
        )}
      </Table.Cell>

      {/* Error-Free Checks */}
      <Table.Cell>
        {isOutOfScope ? (
          <Text size="2" className="text-puntt-neutral-gray-9">
            N/A
          </Text>
        ) : (
          <ProgressBar
            value={task.errorFreeChecks}
            max={100}
            status={task.status}
          />
        )}
      </Table.Cell>

      {/* Status */}
      <Table.Cell className="min-w-[130px]">
        <Flex
          align="center"
          gap="2"
          className="whitespace-nowrap"
          data-testid="roi-status-badge"
        >
          <div
            className={cx("h-2.5 w-2.5 shrink-0 rounded-full", styles.dotColor)}
          />
          <Text size="2">{task.status}</Text>
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
}

export const AutomationTable: FC<AutomationTableProps> = ({
  tasks,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation("ui");
  // Sort tasks by status order
  const sortedTasks = [...tasks].sort((a, b) => {
    return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
  });

  return (
    <Table.Root variant="surface" data-testid="roi-automation-table">
      <Table.Header>
        <Table.Row className="bg-puntt-neutral-gray-3">
          <Table.ColumnHeaderCell className="min-w-[180px] font-medium">
            <Flex align="center" gap="1">
              Task Type
              <Tooltip content="The type of automated review task">
                <Info
                  size={14}
                  className="shrink-0 cursor-help text-puntt-neutral-gray-9"
                />
              </Tooltip>
            </Flex>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            {t("roi.automationTable.tasksDone")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            <Flex align="start" gap="1">
              Low Risk Human Caught Issues
              <Tooltip content="Low risk issues caught by human reviewers">
                <Info
                  size={14}
                  className="shrink-0 cursor-help text-puntt-neutral-gray-9"
                />
              </Tooltip>
            </Flex>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            <Flex align="start" gap="1">
              High Risk Human Caught Issues
              <Tooltip content="High risk issues caught by human reviewers">
                <Info
                  size={14}
                  className="shrink-0 cursor-help text-puntt-neutral-gray-9"
                />
              </Tooltip>
            </Flex>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            <Flex align="start" gap="1">
              Weeks without High Risk Error
              <Tooltip content="Consecutive weeks without high risk errors">
                <Info
                  size={14}
                  className="shrink-0 cursor-help text-puntt-neutral-gray-9"
                />
              </Tooltip>
            </Flex>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            {t("roi.automationTable.aiReviewSpeed")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            {t("roi.automationTable.auditLog")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="font-medium">
            <Flex align="start" gap="1">
              Error-Free Checks
              <Tooltip content="Number of checks completed without errors">
                <Info
                  size={14}
                  className="shrink-0 cursor-help text-puntt-neutral-gray-9"
                />
              </Tooltip>
            </Flex>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="min-w-[130px] font-medium">
            <Flex align="center" gap="1">
              Status
              <Tooltip content="Current automation status of the task">
                <Info
                  size={14}
                  className="shrink-0 cursor-help text-puntt-neutral-gray-9"
                />
              </Tooltip>
            </Flex>
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {sortedTasks.length > 0 ? (
          sortedTasks.map((task, index) => (
            <TaskRow
              key={task.id}
              task={task}
              index={index}
              startDate={startDate}
              endDate={endDate}
            />
          ))
        ) : (
          <Table.Row>
            <Table.Cell colSpan={9} className="py-8 text-center">
              <Text size="2" color="gray">
                {t("roi.automationTable.noTasksAvailable")}
              </Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};
